import { jsx as _jsx } from "react/jsx-runtime";
import { reportErrorToBugsnag } from "@air/utils-error";
import { createContext, memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { isDevOrTestStage } from "~/swr-hooks/utils";
const DEFAULT_VALUE = {
    isOutlineOpen: false,
    setIsOutlineOpen: ()=>{},
    currentPage: 1,
    numPages: 0,
    changePage: ()=>{},
    pdfDocument: null,
    pdfPagesCacheRef: null,
    renderOutlinePageMenu: undefined,
    updatePDFControls: ()=>{}
};
const PDFOutlineContext = /*#__PURE__*/ createContext(DEFAULT_VALUE);
export const PDFOutlineProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const [isOutlineOpen, setIsOutlineOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pdfDocument, setPdfDocument] = useState(null);
    const pdfPagesCacheRef = useRef(null);
    const renderOutlinePageMenuRef = useRef(undefined);
    const changePageRef = useRef(null);
    var _pdfDocument_numPages;
    const numPages = (_pdfDocument_numPages = pdfDocument === null || pdfDocument === void 0 ? void 0 : pdfDocument.numPages) !== null && _pdfDocument_numPages !== void 0 ? _pdfDocument_numPages : 0;
    const changePage = useCallback((pageNumber)=>{
        var _changePageRef_current;
        setCurrentPage(pageNumber);
        (_changePageRef_current = changePageRef.current) === null || _changePageRef_current === void 0 ? void 0 : _changePageRef_current.call(changePageRef, pageNumber);
    }, []);
    const updatePDFControls = useCallback((newPdfControls)=>{
        setIsOutlineOpen(newPdfControls.isOutlineOpen);
        setCurrentPage(newPdfControls.currentPage);
        setPdfDocument(newPdfControls.pdfDocument);
        pdfPagesCacheRef.current = newPdfControls.pdfPagesCacheRef;
        renderOutlinePageMenuRef.current = newPdfControls.renderOutlinePageMenu;
        changePageRef.current = newPdfControls.changePage;
    }, []);
    const contextValue = useMemo(()=>({
            isOutlineOpen,
            setIsOutlineOpen,
            currentPage,
            numPages,
            changePage,
            pdfDocument,
            pdfPagesCacheRef: pdfPagesCacheRef.current,
            renderOutlinePageMenu: renderOutlinePageMenuRef.current,
            updatePDFControls
        }), [
        isOutlineOpen,
        setIsOutlineOpen,
        currentPage,
        numPages,
        changePage,
        pdfDocument,
        updatePDFControls
    ]);
    return /*#__PURE__*/ _jsx(PDFOutlineContext.Provider, {
        value: contextValue,
        children: children
    });
});
PDFOutlineProvider.displayName = "PDFOutlineProvider";
export const usePDFOutlineContext = ()=>{
    const context = useContext(PDFOutlineContext);
    if (context === DEFAULT_VALUE) {
        const error = "usePDFOutlineContext must be used within a PDFOutlineProvider";
        if (isDevOrTestStage()) {
            throw new Error(error);
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
